/**
 * ตรรกะล้วนของหน้าจอบันทึกการเปลี่ยนสิทธิ์ — แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * แนวเดียวกับ `permissionMatrixView.ts` — สิ่งที่อยู่ที่นี่คือคำถามที่ตอบได้โดยไม่
 * ต้องเรนเดอร์อะไรเลย
 *
 * **กติกาข้อเดียวที่ครอบทั้งไฟล์นี้: ไม่ตีความแทนหลักฐาน** contract ระบุว่า
 * `before_json`/`after_json` ถูกส่งผ่านมาตามที่เก็บ โดยผู้เขียนแต่ละครั้งเป็นคน
 * กำหนดรูป การ normalise ให้เป็นรูปเดียวกันคือการที่หน้าจอตัดสินแทนว่าการเปลี่ยน
 * แปลงในอดีตแปลว่าอะไร ซึ่ง audit trail ต้องไม่ทำ
 */

import type { AuditSnapshot, RbacAuditEntry } from '../../lib/rbacAdmin/types';

/**
 * ข้อความแทน snapshot ที่เป็น `null`
 *
 * `null` ที่นี่เป็น **ข้อเท็จจริง** ไม่ใช่ข้อมูลที่หายไป: `before_json` เป็น `null`
 * เมื่อสิ่งนั้นยังไม่เคยมีอยู่ก่อนการเปลี่ยนแปลง (สร้างแผนกใหม่, ตั้งสิทธิ์ให้ module
 * ที่ยังไม่เคยมีแถว) — ช่องว่างเปล่า ๆ จะอ่านเหมือนหน้าจอโหลดไม่ครบ
 */
export const NO_SNAPSHOT_LABEL = 'ไม่มี';

/** ข้อความแทนแถวที่ไม่ได้ระบุเป้าหมายรายคน/รายแผนกไว้เลย */
export const NO_TARGET_LABEL = 'ไม่ระบุเป้าหมาย';

/** ข้อความแทน `module_code` ที่เป็น `null` — การเปลี่ยน role ไม่ผูกกับโมดูลใด */
export const NO_MODULE_LABEL = 'ไม่ผูกกับโมดูล';

/**
 * ชื่อภาษาไทยของ `action` ที่ backend เขียนลง `rbac_audit_log`
 *
 * รหัสที่ไม่อยู่ในตารางนี้ถูกแสดงเป็นรหัสดิบ ไม่ใช่ถูกซ่อนหรือแทนที่ด้วย
 * "ไม่ทราบ" — วันที่มี action ชนิดใหม่ ผู้ตรวจสอบต้องยังเห็นว่ามันเกิดขึ้น
 */
export const AUDIT_ACTION_LABELS: Readonly<Record<string, string>> = {
  CREATE: 'สร้าง',
  UPDATE: 'แก้ไข',
  DELETE: 'ลบ',
};

export function actionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

export interface AuditTargetPart {
  readonly kind: 'employee' | 'department';
  readonly label: string;
  readonly value: string;
}

/**
 * เป้าหมายของแถวนี้ — **ทุกเป้าหมายที่แถวนั้นระบุ ไม่ใช่อันแรกที่เจอ**
 *
 * ในทางปฏิบัติแถวหนึ่งมีเป้าหมายเดียว (การเปลี่ยน role ระบุพนักงาน การแก้ตาราง
 * สิทธิ์ระบุแผนก) แต่ฟังก์ชันนี้ไม่ตัดอันที่สองทิ้งถ้ามันมีมาจริง — หน้าจอ audit
 * ที่เลือกแสดงแค่บางส่วนของหลักฐานที่ได้รับ คือหน้าจอที่ตอบคำถามของผู้ตรวจสอบ
 * ไม่ครบโดยที่เขาไม่รู้ตัว
 *
 * รายการว่างแปลว่าแถวนั้นไม่ได้ระบุเป้าหมายรายคน/รายแผนกไว้ ซึ่งเป็นสถานะที่ชนิด
 * ข้อมูลอนุญาต จึงต้องมีข้อความของมันเอง ไม่ใช่ช่องว่าง
 */
export function auditTargets(entry: RbacAuditEntry): readonly AuditTargetPart[] {
  const targets: AuditTargetPart[] = [];
  if (entry.target_employee_id !== null) {
    targets.push({ kind: 'employee', label: 'พนักงาน', value: entry.target_employee_id });
  }
  if (entry.target_department_id !== null) {
    targets.push({ kind: 'department', label: 'แผนก', value: entry.target_department_id });
  }
  return targets;
}

/**
 * ค่าหนึ่งค่าใน snapshot ให้อยู่ในรูปที่อ่านออก — **โดยไม่แปลความหมาย**
 *
 * `JSON.stringify` ตรง ๆ เพราะค่าเหล่านี้มาจากหลายชนิดของการเปลี่ยนแปลง (boolean
 * ของ flag, array ของ role, สตริงของชื่อแผนก) การแปลง `false` เป็น "ไม่ได้รับสิทธิ์"
 * จะถูกสำหรับ flag แต่ผิดทันทีที่ snapshot ชนิดอื่นมีค่า `false` ด้วยเหตุผลอื่น
 */
export function formatSnapshotValue(value: unknown): string {
  return JSON.stringify(value) ?? 'null';
}

/** คู่ (คีย์, ค่า) ของ snapshot ตามลำดับที่เซิร์ฟเวอร์ส่งมา — ไม่เรียงใหม่ */
export function snapshotEntries(
  snapshot: AuditSnapshot,
): readonly (readonly [string, string])[] {
  return Object.entries(snapshot).map(
    ([key, value]) => [key, formatSnapshotValue(value)] as const,
  );
}

/**
 * snapshot นี้มีอะไรให้แสดงหรือไม่
 *
 * object ว่าง (`{}`) ต่างจาก `null` โดยสิ้นเชิง: อันแรกแปลว่า "มีสถานะบันทึกไว้ และ
 * สถานะนั้นไม่มีฟิลด์ใดเลย" ส่วนอันหลังแปลว่า "ตอนนั้นยังไม่มีสิ่งนี้อยู่" — ทั้งคู่
 * แสดงเป็นช่องว่างเหมือนกันไม่ได้
 */
export function hasSnapshot(snapshot: AuditSnapshot | null): snapshot is AuditSnapshot {
  return snapshot !== null;
}
